import * as THREE from 'three';
import MoveGizmoPartEnum from '../../enums/MoveGizmoPartEnum';
import ICanvasInputListener from '../../logic/ICanvasInputListener';
import { isPlaneEdgeOn, isViewAlongAxis } from '../../utils/editHandleVisibility';
import { disposeGroup } from '../../utils/disposeUtils';

type MoveGizmoPartKind = 'axis' | 'plane';

interface MoveGizmoPartEntry {
    part: MoveGizmoPartEnum;
    kind: MoveGizmoPartKind;
    group: THREE.Group;
    // Drag axis for an arrow, plane normal for a quad — in gizmo-local space, so the model rotation
    // baked into the root group's quaternion applies to it exactly once.
    localDirection: THREE.Vector3;
    // The two arrows a quad spans — the directions a planar move is free in. Drives both the
    // reference lines the drag snaps to and the arrows lit alongside the quad on hover. Arrows
    // have none: their own direction is already the only thing to align with.
    planeAxisParts?: [MoveGizmoPartEnum, MoveGizmoPartEnum];
    // Visible materials only. The invisible pick meshes are deliberately left out so hover styling
    // can never make them show up.
    materials: THREE.MeshBasicMaterial[];
    baseColor: number;
    baseOpacity: number;
    hoverOpacity: number;
}

// A three-axis translate gizmo: an arrow per axis plus a quad in each corner between two axes for
// planar moves. Built at unit scale (arrow length 1) and rescaled every frame so it stays the same
// size on screen no matter the camera distance or orthographic zoom.
//
// Like EditHandleGeometry, the gizmo stays on the default layer (0), which SceneDirector enables on
// both camera and raycaster in every viewer mode — that's what makes it visible and clickable in
// the 2D floorplan and in 3D alike, with no ViewerMode change.
export default class MoveGizmoHelper {

    // Arrow length on screen. Everything else is a fraction of the unit-length arrow.
    private static readonly GIZMO_LENGTH_PX = 90;
    private static readonly AXIS_LENGTH = 1;
    private static readonly HEAD_LENGTH = 0.28;
    private static readonly HEAD_RADIUS = 0.09;
    private static readonly SHAFT_RADIUS = 0.022;
    // Generous invisible click margin around the thin arrow, as required for any new pickable
    // scene geometry — the visible shaft alone is far too fiddly to hit.
    private static readonly HIT_RADIUS = 0.13;
    private static readonly PLANE_SIZE = 0.3;
    // Distance from the origin to the quad's centre along each of its two axes. Kept clear of the
    // shafts so a plane and an arrow never overlap on screen.
    private static readonly PLANE_OFFSET = 0.42;

    private static readonly COLOR_X = 0xD32F2F;
    private static readonly COLOR_Y = 0x388E3C;
    private static readonly COLOR_Z = 0x1565C0;
    private static readonly COLOR_HOVER = 0xFFC400;

    private static readonly ARROW_OPACITY = 0.95;
    private static readonly ARROW_HOVER_OPACITY = 1;
    private static readonly PLANE_OPACITY = 0.35;
    // A hovered quad lifts but stays see-through: it covers real estate an arrow doesn't, and going
    // fully opaque would hide the model underneath just as the user is aiming a move.
    private static readonly PLANE_HOVER_OPACITY = 0.6;
    // What every part that isn't highlighted drops to, so the grabbable one reads unambiguously.
    private static readonly FADED_OPACITY = 0.12;

    // Above every RenderOrderEnum value, matching EditHandleGeometry: the gizmo must never be
    // hidden by the model it is used to move.
    private static readonly RENDER_ORDER = 999;

    private static readonly HIT_MESH_NAME = 'HitBoxMesh';

    #canvasInputListener: ICanvasInputListener;
    #group: THREE.Group = new THREE.Group();
    #parts: MoveGizmoPartEntry[] = [];
    #partsByGroup: Map<THREE.Object3D, MoveGizmoPartEntry> = new Map();
    #hitMaterial: THREE.MeshBasicMaterial;
    #origin: THREE.Vector3 = new THREE.Vector3();
    #hoveredPart: MoveGizmoPartEnum | undefined;
    #enabled = false;
    #animationFrameId: number | null = null;

    constructor(canvasInputListener: ICanvasInputListener, modelRotationOffset: number) {
        this.#canvasInputListener = canvasInputListener;

        this.#group.name = 'MoveGizmo';
        this.#group.renderOrder = MoveGizmoHelper.RENDER_ORDER;
        this.#group.visible = false;
        // Rotating the whole gizmo about world Z is what aligns X and Y with the model rather than
        // with world axes, the same correction InputCommand's reference axes and buildPolygonRefAxes
        // apply. Z is unaffected by an offset about Z.
        this.#group.quaternion.setFromAxisAngle(new THREE.Vector3(0, 0, 1), modelRotationOffset);

        // Zero opacity but visible === true: isFullyVisible() drops invisible objects from raycast
        // results, so visible = false would make these pick targets unclickable.
        this.#hitMaterial = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthTest: false,
            depthWrite: false
        });
        this.#hitMaterial.name = 'MoveGizmoHitMaterial';

        const axisX = new THREE.Vector3(1, 0, 0);
        const axisY = new THREE.Vector3(0, 1, 0);
        const axisZ = new THREE.Vector3(0, 0, 1);

        this.#buildArrow(MoveGizmoPartEnum.AxisX, axisX, MoveGizmoHelper.COLOR_X);
        this.#buildArrow(MoveGizmoPartEnum.AxisY, axisY, MoveGizmoHelper.COLOR_Y);
        this.#buildArrow(MoveGizmoPartEnum.AxisZ, axisZ, MoveGizmoHelper.COLOR_Z);

        // Built after the arrows: a quad takes its two spanning directions from them, so the
        // arrows, the reference lines and the drag constraint can never disagree.
        this.#buildPlane(MoveGizmoPartEnum.PlaneXY,
            [MoveGizmoPartEnum.AxisX, MoveGizmoPartEnum.AxisY], MoveGizmoHelper.COLOR_Z);
        this.#buildPlane(MoveGizmoPartEnum.PlaneYZ,
            [MoveGizmoPartEnum.AxisY, MoveGizmoPartEnum.AxisZ], MoveGizmoHelper.COLOR_X);
        this.#buildPlane(MoveGizmoPartEnum.PlaneZX,
            [MoveGizmoPartEnum.AxisZ, MoveGizmoPartEnum.AxisX], MoveGizmoHelper.COLOR_Y);
    }

    #createPartMaterial(color: number, opacity: number): THREE.MeshBasicMaterial {
        // Unlit on purpose: Stage2d and Stage3d install different lighting rigs, and a lit gizmo
        // would read as a different colour in each. depthTest off plus the high render order keeps
        // it drawn over the model.
        return new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity,
            depthTest: false,
            depthWrite: false
        });
    }

    #registerPart(entry: MoveGizmoPartEntry): void {
        entry.group.renderOrder = MoveGizmoHelper.RENDER_ORDER;
        entry.group.traverse(child => { child.renderOrder = MoveGizmoHelper.RENDER_ORDER; });
        this.#parts.push(entry);
        this.#partsByGroup.set(entry.group, entry);
        this.#group.add(entry.group);
    }

    #buildArrow(part: MoveGizmoPartEnum, axis: THREE.Vector3, color: number): void {
        const group = new THREE.Group();
        group.name = `MoveGizmoArrow-${MoveGizmoPartEnum[part]}`;
        // +Y is the pointing direction for cones repo-wide (see LengthDimensionSubGeometryBase's
        // end markers and ChecklistItemSubGeometry3d's pin).
        group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis);

        const material = this.#createPartMaterial(color, MoveGizmoHelper.ARROW_OPACITY);
        const shaftLength = MoveGizmoHelper.AXIS_LENGTH - MoveGizmoHelper.HEAD_LENGTH;

        const shaft = new THREE.Mesh(
            new THREE.CylinderGeometry(MoveGizmoHelper.SHAFT_RADIUS, MoveGizmoHelper.SHAFT_RADIUS, shaftLength, 12),
            material);
        shaft.position.y = shaftLength / 2;

        const head = new THREE.Mesh(
            new THREE.ConeGeometry(MoveGizmoHelper.HEAD_RADIUS, MoveGizmoHelper.HEAD_LENGTH, 16),
            material);
        head.position.y = shaftLength + MoveGizmoHelper.HEAD_LENGTH / 2;

        const hitMesh = new THREE.Mesh(
            new THREE.CylinderGeometry(MoveGizmoHelper.HIT_RADIUS, MoveGizmoHelper.HIT_RADIUS, MoveGizmoHelper.AXIS_LENGTH, 8),
            this.#hitMaterial);
        hitMesh.name = MoveGizmoHelper.HIT_MESH_NAME;
        hitMesh.position.y = MoveGizmoHelper.AXIS_LENGTH / 2;

        group.add(shaft, head, hitMesh);

        this.#registerPart({
            part,
            kind: 'axis',
            group,
            localDirection: axis.clone(),
            materials: [material],
            baseColor: color,
            baseOpacity: MoveGizmoHelper.ARROW_OPACITY,
            hoverOpacity: MoveGizmoHelper.ARROW_HOVER_OPACITY
        });
    }

    #buildPlane(part: MoveGizmoPartEnum,
                axisParts: [MoveGizmoPartEnum, MoveGizmoPartEnum],
                color: number): void {
        const u = this.#findPart(axisParts[0])!.localDirection.clone();
        const v = this.#findPart(axisParts[1])!.localDirection.clone();
        const normal = new THREE.Vector3().crossVectors(u, v).normalize();

        const group = new THREE.Group();
        group.name = `MoveGizmoPlane-${MoveGizmoPartEnum[part]}`;

        const material = this.#createPartMaterial(color, MoveGizmoHelper.PLANE_OPACITY);
        // Visible and pickable from either side — in 3D the user can orbit behind the gizmo.
        material.side = THREE.DoubleSide;

        // The quad is generous enough to click on its own, so it needs no separate hit geometry.
        const mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(MoveGizmoHelper.PLANE_SIZE, MoveGizmoHelper.PLANE_SIZE),
            material);
        // PlaneGeometry lies in XY with a +Z normal, so mapping (X, Y, Z) onto (u, v, normal) lands
        // it in the plane those two axes span.
        mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(u, v, normal));
        mesh.position.copy(u.clone().add(v).multiplyScalar(MoveGizmoHelper.PLANE_OFFSET));

        group.add(mesh);

        this.#registerPart({
            part,
            kind: 'plane',
            group,
            localDirection: normal,
            planeAxisParts: axisParts,
            materials: [material],
            baseColor: color,
            baseOpacity: MoveGizmoHelper.PLANE_OPACITY,
            hoverOpacity: MoveGizmoHelper.PLANE_HOVER_OPACITY
        });
    }

    addToScene(scene: THREE.Scene): void {
        scene.add(this.#group);
    }

    /** The groups to hand to CanvasInputListener.setTargetObjects so the gizmo is pickable. */
    getPartGroups(): THREE.Object3D[] {
        return this.#parts.map(p => p.group);
    }

    /** Walks up from a raycast hit to the gizmo part that owns it, if any. */
    getPartFromObject3D(object: THREE.Object3D | null): MoveGizmoPartEnum | undefined {
        let current: THREE.Object3D | null = object;
        while (current) {
            const entry = this.#partsByGroup.get(current);
            if (entry) return entry.part;
            current = current.parent;
        }
        return undefined;
    }

    isAxisPart(part: MoveGizmoPartEnum): boolean {
        return this.#findPart(part)?.kind === 'axis';
    }

    /** Drag axis of an arrow, or plane normal of a quad, in world space. */
    getWorldDirection(part: MoveGizmoPartEnum): THREE.Vector3 {
        const entry = this.#findPart(part);
        if (!entry) return new THREE.Vector3();

        return entry.localDirection.clone().applyQuaternion(this.#group.quaternion).normalize();
    }

    /** The two in-plane directions a quad spans, in world space. Empty for an arrow. */
    getWorldPlaneAxes(part: MoveGizmoPartEnum): THREE.Vector3[] {
        const entry = this.#findPart(part);
        if (!entry?.planeAxisParts) return [];

        return entry.planeAxisParts.map(axisPart => this.getWorldDirection(axisPart));
    }

    getOrigin(): THREE.Vector3 {
        return this.#origin.clone();
    }

    setOrigin(origin: THREE.Vector3): void {
        this.#origin.copy(origin);
        this.#group.position.copy(origin);
        this.#updateScale();
    }

    // Isolates what a click would grab: the highlighted parts lift into the highlight colour while
    // everything else fades almost out, so the directions the selection is about to be free in are
    // obvious before committing to the drag.
    setHovered(part: MoveGizmoPartEnum | undefined): void {
        if (part === this.#hoveredPart) return;
        this.#hoveredPart = part;

        const highlighted = this.#highlightedParts(part);
        for (const entry of this.#parts) {
            const isHighlighted = highlighted.has(entry.part);
            const isFaded = part !== undefined && !isHighlighted;
            for (const material of entry.materials) {
                material.color.setHex(isHighlighted ? MoveGizmoHelper.COLOR_HOVER : entry.baseColor);
                material.opacity = isFaded ? MoveGizmoHelper.FADED_OPACITY
                    : isHighlighted ? entry.hoverOpacity
                    : entry.baseOpacity;
            }
        }
    }

    // Hovering a quad lights the two arrows it spans as well as the quad itself: those arrows are
    // exactly the directions a planar move is free in, so highlighting them says what the move will
    // do far more directly than the quad alone. An arrow highlights only itself — a move along one
    // axis is not a move in any plane, so lighting the quads that contain it would overstate it.
    #highlightedParts(part: MoveGizmoPartEnum | undefined): Set<MoveGizmoPartEnum> {
        const highlighted = new Set<MoveGizmoPartEnum>();
        if (part === undefined) return highlighted;

        highlighted.add(part);
        this.#findPart(part)?.planeAxisParts?.forEach(axisPart => highlighted.add(axisPart));
        return highlighted;
    }

    // Drops the parts that are degenerate from the current viewpoint, reusing the edit-handle
    // predicates: an arrow pointing at the camera can't be dragged meaningfully, and a quad seen
    // edge-on is a line. In the top-down 2D floorplan this leaves exactly the X and Y arrows and
    // the XY plane, with no viewer-mode special case.
    setVisiblePartsForView(worldViewDirection: THREE.Vector3): void {
        for (const entry of this.#parts) {
            const direction = entry.localDirection.clone().applyQuaternion(this.#group.quaternion);
            entry.group.visible = entry.kind === 'axis'
                ? !isViewAlongAxis(worldViewDirection, direction)
                : !isPlaneEdgeOn(worldViewDirection, direction);
        }
    }

    enable(): void {
        if (this.#enabled) return;
        this.#enabled = true;
        this.#group.visible = true;
        this.#updateScale();
        this.#tick();
    }

    disable(): void {
        this.#enabled = false;
        this.#group.visible = false;
        this.#stopAnimation();
    }

    dispose(): void {
        this.#stopAnimation();
        this.#partsByGroup.clear();
        this.#parts = [];
        // disposeGroup walks the whole tree and disposes the materials it finds, which is safe here
        // because every gizmo material is owned by this instance rather than shared statically.
        disposeGroup(this.#group);
    }

    #findPart(part: MoveGizmoPartEnum): MoveGizmoPartEntry | undefined {
        return this.#parts.find(p => p.part === part);
    }

    #updateScale(): void {
        const worldUnitsPerPixel = this.#canvasInputListener.getWorldUnitsPerPixelAt(this.#origin);
        // Zero means there's no camera or canvas size yet — keep the previous scale rather than
        // collapsing the gizmo to nothing.
        if (worldUnitsPerPixel <= 0) return;

        this.#group.scale.setScalar(worldUnitsPerPixel * MoveGizmoHelper.GIZMO_LENGTH_PX);
    }

    // Zoom and dolly change the world-units-per-pixel continuously, and camera-controls only reports
    // once it comes to rest, so the rescale runs per frame while the gizmo is up (the same
    // self-driven loop PlaneGridHelper uses for its pulse).
    #tick = () => {
        if (!this.#enabled) return;

        this.#updateScale();
        this.#animationFrameId = requestAnimationFrame(this.#tick);
    };

    #stopAnimation(): void {
        if (this.#animationFrameId !== null) {
            cancelAnimationFrame(this.#animationFrameId);
            this.#animationFrameId = null;
        }
    }
}
